import {theme as antdTheme} from 'antd';
import type {ThemeConfig} from 'antd';
import {
  CAP_ACCENT,
  CAP_BIFROST_BORDER,
  CAP_DARK,
  CAP_FONT_COLOR,
  CAP_GREY,
  CAP_NEUTRAL,
  CAP_PRIMARY,
  CAP_SECONDARY,
  CAP_SEMANTIC,
} from './capTokens';

export type ThemeMode = 'light' | 'dark';

/**
 * Bridges the app's existing per-tenant branding JSON (public/theme/{tenantID}.json,
 * consumed today by CSfetchGlobalData.ts to build the MUI theme) into an antd v6
 * ThemeConfig, so components migrated to antd stay visually consistent with the
 * tenant's brand without a second, hand-maintained source of truth.
 *
 * `branding` is the same shape CSfetchGlobalData already works with:
 *  - palette: the flat MUI-style palette used for `muiTheme.palette = palette`
 *  - CSTheme: the nested `configurations.theme` object (typography/shape/etc.)
 *
 * COLOR PRECEDENCE — explicit product choice: the Cap UI Library palette
 * (capTokens.ts) is forced for every antd color token, overriding whatever a
 * tenant's theme JSON defines for primary/secondary/error/warning/success/text/
 * background/border. This makes every client look like Capillary's own design
 * system rather than that tenant's brand color (e.g. Connect's blue #269bdd is
 * no longer shown). Other non-color tokens (fontFamily, heading sizes) are
 * unaffected and still come from the tenant's CSTheme as before; borderRadius
 * is the one exception — it stays tenant-overridable, but when a tenant
 * doesn't set one it now defaults to BiFrost's 4px (CAP_BIFROST_BORDER) rather
 * than antd's stock 6px, matching the color-forcing rationale above.
 */

interface PaletteColor {
  main?: string;
  contrastText?: string;
}

interface BrandingPalette {
  primary?: PaletteColor;
  secondary?: PaletteColor;
  error?: PaletteColor;
  warning?: PaletteColor;
  success?: PaletteColor;
  text?: {primary?: string; secondary?: string};
  background?: {default?: string; paper?: string};
}

interface BrandingCSTheme {
  palette?: {divider?: string};
  shape?: {borderRadius?: number};
  typography?: {
    fontFamily?: string;
    h1?: {fontSize?: string};
    h2?: {fontSize?: string};
    h3?: {fontSize?: string};
    h4?: {fontSize?: string};
    h5?: {fontSize?: string};
  };
}

interface BrandingSource {
  palette?: BrandingPalette;
  CSTheme?: BrandingCSTheme;
}

// tenant typography sizes are expressed in rem (e.g. "2.0rem"); antd's
// Typography component tokens want raw px numbers.
const remToPx = (value?: string): number | undefined => {
  if (!value) return undefined;
  const rem = parseFloat(value);
  return Number.isNaN(rem) ? undefined : rem * 16;
};

const buildAntdTheme = (branding: BrandingSource = {}, mode: ThemeMode = 'light'): ThemeConfig => {
  const {CSTheme = {}} = branding;
  const borderRadius = CSTheme.shape?.borderRadius;
  const fontFamily = CSTheme.typography?.fontFamily;
  const isDark = mode === 'dark';

  // Loosely typed so we can also carry a few custom, non-standard-antd
  // keys (colorSecondary/colorOnSecondary/colorAccent) through theme.useToken()
  // for components (e.g. Footer) that need a palette color with no standard
  // antd token equivalent.
  //
  // Cap UI Library colors are forced (not just fallbacks) — see the file-level
  // comment above. `palette`/`borderColor` are intentionally unused for color
  // tokens now; kept as params since non-color tokens below still read CSTheme.
  //
  // LIGHT/DARK — brand colors (primary/secondary/accent/semantic) stay fixed
  // in both modes; they're the same constants regardless of theme. The
  // neutral/background/text/border tokens are a different story: the CAP_GREY/
  // CAP_FONT_COLOR values below are tuned for a light surface (dark text on a
  // white/near-white background) and Capillary doesn't yet publish an official
  // dark-mode palette, so in dark mode we deliberately leave them unset and let
  // antd's own darkAlgorithm derive a full, contrast-safe neutral ramp instead
  // of inventing an unofficial dark palette by hand.
  const token: Record<string, unknown> = {
    colorPrimary: CAP_PRIMARY.base,
    colorSecondary: CAP_SECONDARY.base,
    colorOnSecondary: CAP_NEUTRAL.black,
    // Orange accent/highlight color — no standard antd slot fits this (it's
    // not tied to "warning" semantics), so it's a custom token like colorSecondary.
    colorAccent: CAP_ACCENT.orange,
    colorError: CAP_SEMANTIC.error,
    colorWarning: CAP_SEMANTIC.warning,
    colorSuccess: CAP_SEMANTIC.success,
    colorInfo: CAP_SEMANTIC.info,
    ...(!isDark && {
      // Pale green override for antd's own "success background" map token (used
      // behind success Tags/Alerts/Badges) — narrower than overriding colorSuccess.
      // Light-mode-tuned like the rest of this block; dark mode lets the algorithm derive it.
      colorSuccessBg: CAP_ACCENT.successBackground,
      colorTextBase: CAP_FONT_COLOR.primary,
      colorTextSecondary: CAP_FONT_COLOR.secondary,
      colorBgLayout: CAP_GREY.G09,
      colorBgContainer: CAP_NEUTRAL.white,
      // BiFrost-matched (see CAP_BIFROST_BORDER) — confirmed identical across
      // Input, Select, Card, Button and Dropdown on the BiFrost dashboard, so
      // it's forced globally rather than per-component like the rest of this block.
      colorBorder: CAP_BIFROST_BORDER.color,
      // Card reads colorBorderSecondary, not colorBorder, for its own border —
      // confirmed the same #DDE3EE on BiFrost's Card, so both tokens are set.
      colorBorderSecondary: CAP_BIFROST_BORDER.color,
      // Focus ring shown on Input/Select/Checkbox/etc. — BiFrost's is a
      // darker, more saturated green than colorPrimary at the antd-default alpha.
      controlOutline: CAP_BIFROST_BORDER.focusOutline,
    }),
    ...(isDark && {
      // Seeds antd's darkAlgorithm (see colorBgBase in capTokens.ts) so every
      // derived dark surface — colorBgLayout, colorBgContainer, colorBgElevated,
      // colorBorder, etc. — comes out as a shade of this navy instead of antd's
      // default near-black.
      colorBgBase: CAP_DARK.bgBase,
      // Pinned explicitly rather than left to the algorithm's lighten-from-
      // bgBase derivation — see the comment on CAP_DARK.surface.
      colorBgContainer: CAP_DARK.surface,
      colorBgElevated: CAP_DARK.surface,
      // Floating panels (DatePicker, Select dropdown, Dropdown menu, Popover,
      // Modal, ...) use boxShadowSecondary for their only depth cue, and
      // antd's own dark-mode shadow is a near-invisible white glow (~2%
      // opacity) — against a flat dark page it reads as no separation at all
      // ("panel merges into the background"). Adding a crisp 1px border ring
      // (reusing the same colorBorder every input/card border already uses,
      // via its CSS var so it always matches) plus a real dark shadow fixes
      // that regardless of how close the two navy tones are perceptually.
      // The var() fallback (only used if CSS custom properties are somehow
      // unavailable) points at CAP_DARK.surface rather than a hand-computed
      // hex, so it can't drift out of sync if CAP_DARK.bgBase ever changes.
      // The rgba(0,0,0,X) layers are plain shadow-depth/opacity tuning, not
      // brand colors, so they're left as literals like any standard shadow recipe.
      boxShadowSecondary: `
        0 0 0 1px var(--ant-color-border, ${CAP_DARK.surface}),
        0 6px 16px 0 rgba(0, 0, 0, 0.48),
        0 3px 6px -4px rgba(0, 0, 0, 0.56),
        0 9px 28px 8px rgba(0, 0, 0, 0.36)
      `,
    }),
    // Falls back to BiFrost's 4px when the tenant doesn't specify its own
    // shape — see the file-level comment. Not gated by isDark: it's a shape
    // choice, not a color one, so it applies the same in both modes.
    borderRadius: typeof borderRadius === 'number' ? borderRadius : CAP_BIFROST_BORDER.radius,
    ...(fontFamily && {fontFamily}),
  };

  // Input/Select/DatePicker/Button/Card/Dropdown (and the Password/TextArea/
  // RangePicker variants that share Input/DatePicker's tokens) are pinned to
  // BiFrost's 4px regardless of the tenant's global `borderRadius` above —
  // confirmed identical across these on the BiFrost dashboard (see the
  // colorBorder comment below), so a tenant like the ones in
  // public/theme/*.json that sets shape.borderRadius to 15 shouldn't round
  // them any more than that.
  const bifrostRadius = {
    borderRadius: CAP_BIFROST_BORDER.radius,
    borderRadiusLG: CAP_BIFROST_BORDER.radius,
    borderRadiusSM: CAP_BIFROST_BORDER.radius,
  };

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token,
    components: {
      Input: bifrostRadius,
      Select: bifrostRadius,
      DatePicker: bifrostRadius,
      Button: bifrostRadius,
      Card: bifrostRadius,
      Dropdown: bifrostRadius,
      Typography: {
        ...(remToPx(CSTheme.typography?.h1?.fontSize) && {
          fontSizeHeading1: remToPx(CSTheme.typography?.h1?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h2?.fontSize) && {
          fontSizeHeading2: remToPx(CSTheme.typography?.h2?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h3?.fontSize) && {
          fontSizeHeading3: remToPx(CSTheme.typography?.h3?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h4?.fontSize) && {
          fontSizeHeading4: remToPx(CSTheme.typography?.h4?.fontSize),
        }),
        ...(remToPx(CSTheme.typography?.h5?.fontSize) && {
          fontSizeHeading5: remToPx(CSTheme.typography?.h5?.fontSize),
        }),
      },
    },
  };
};

export default buildAntdTheme;
